/**
 * Payroll sub-tab — month preview + batch post.
 *
 * For the selected month, shows each active staff with present /
 * half / absent counts and the computed payable amount. Tap
 * "Post payroll" to atomically:
 *   1. Write one `orgFinances` entry per staff (category=salary)
 *   2. Stamp `lastPayrollMonth = YYYY-MM` on each staff doc
 *
 * The batch is idempotent — re-posting the same month skips
 * already-posted staff. Zero-amount rows still mark the month
 * done so the button hides for fully-absent staff.
 *
 * Web mirror of mobile `src/features/finance/tabs/PayrollTab.tsx`.
 */
import { useMemo, useState } from 'react';

import {
  buildPayrollPreview,
  monthKey,
  postMonthlyPayroll,
  useStaff,
  useStaffAttendance,
} from '@/lib/staff';

function fmtInr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function shiftMonth(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

export function PayrollTab({ orgId }: { orgId: string | null }) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const { data: staff, loading: staffLoading } = useStaff(orgId);
  const { byStaff, loading: attLoading } = useStaffAttendance(orgId, month);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo(
    () => (staffLoading || attLoading ? [] : buildPayrollPreview(staff, byStaff)),
    [staff, byStaff, staffLoading, attLoading],
  );

  const total = preview.reduce((sum, r) => sum + r.amount, 0);
  const mk = monthKey(month);
  const allPosted =
    preview.length > 0 && preview.every((r) => r.staff.lastPayrollMonth === mk);

  async function post() {
    if (!orgId) return;
    if (!confirm(`Post payroll for ${monthLabel(month)}? Total: ${fmtInr(total)}`)) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await postMonthlyPayroll({ orgId, month, rows: preview });
      setResult(
        `Posted ${out.posted} staff${out.posted === 1 ? '' : ''}. ` +
          (out.skipped ? `${out.skipped} skipped (already posted / zero).` : ''),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not post payroll.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Month selector + total */}
      <div
        className="card"
        style={{
          padding: 14,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          aria-label="Previous month"
        >
          <i className="ph ph-caret-left" />
        </button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{monthLabel(month)}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
            Total payable: <strong>{fmtInr(total)}</strong>
          </div>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          aria-label="Next month"
        >
          <i className="ph ph-caret-right" />
        </button>
      </div>

      {/* Status banner */}
      {result ? (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            background: '#DCFCE7',
            color: '#166534',
            fontSize: 13,
          }}
        >
          {result}
        </div>
      ) : null}
      {error ? (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            background: '#FEE2E2',
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* Table */}
      {staffLoading || attLoading ? (
        <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : preview.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <i className="ph ph-money" style={{ fontSize: 48, opacity: 0.5 }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No active staff</div>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            Add staff in the <strong>Staff</strong> tab to run payroll.
          </div>
        </div>
      ) : (
        <div
          className="card"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.6fr 0.5fr 0.5fr 0.5fr 1fr 0.5fr',
              padding: '10px 14px',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface-muted, #f8fafc)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            <div>Staff</div>
            <div style={{ textAlign: 'right' }}>Present</div>
            <div style={{ textAlign: 'right' }}>Half</div>
            <div style={{ textAlign: 'right' }}>Absent</div>
            <div style={{ textAlign: 'right' }}>Payable</div>
            <div style={{ textAlign: 'right' }}>Status</div>
          </div>
          {preview.map((row) => {
            const posted = row.staff.lastPayrollMonth === mk;
            return (
              <div
                key={row.staff.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.6fr 0.5fr 0.5fr 0.5fr 1fr 0.5fr',
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--border)',
                  alignItems: 'center',
                  fontSize: 13,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{row.staff.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {row.staff.role} · {fmtInr(row.staff.monthlySalary)}/mo
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>{row.presentDays}</div>
                <div style={{ textAlign: 'right' }}>{row.halfDays}</div>
                <div style={{ textAlign: 'right' }}>{row.absentDays}</div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>
                  {fmtInr(row.amount)}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {posted ? (
                    <span
                      style={{
                        padding: '3px 8px',
                        borderRadius: 999,
                        background: '#DCFCE7',
                        color: '#166534',
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      POSTED
                    </span>
                  ) : (
                    <span
                      style={{
                        padding: '3px 8px',
                        borderRadius: 999,
                        background: '#FEF3C7',
                        color: '#B45309',
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    >
                      PENDING
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {preview.length > 0 ? (
        <button
          type="button"
          className="btn-primary"
          onClick={post}
          disabled={busy || allPosted}
          style={{
            padding: '12px 20px',
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {busy
            ? 'Posting…'
            : allPosted
              ? 'Already posted for this month'
              : `Post payroll for ${monthLabel(month)} (${fmtInr(total)})`}
        </button>
      ) : null}

      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>
        Posting creates one salary entry per staff in Expenses and stamps the month done. Re-posting skips already-posted rows.
      </div>
    </div>
  );
}
